/**
 * Bildirishnomalar qo'ng'irog'i — PP-905.
 *
 * QO'NG'IROQCHA QOBIQDA, ALOHIDA SAHIFADA EMAS: ogohlantirishning ma'nosi —
 * foydalanuvchi uni QIDIRMASDAN ko'rishi. Alohida sahifaga yashirilgan
 * ogohlantirish PP-901/902 ning maqsadini bajarmaydi.
 *
 * DAVRIY SO'ROV (`POLL_INTERVAL_MS`), WebSocket emas: bitta so'rov 30
 * soniyada — bu server uchun sezilarli yuk emas, WebSocket esa ulanish
 * holati, qayta ulanish va proksi sozlamalarini olib keladi. Real vaqt
 * (soniyalar) talabi yo'q: PP-901 «≤ 15 daqiqada» deydi.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import { Link } from "react-router-dom";

import { notificationsApi } from "../api/client";
import type { NotificationItem } from "../api/client";
import { NOTIFICATION_TYPE_STYLE, formatRelative } from "../theme/tokens";
import { Bell } from "lucide-react";
import { Badge, Button, EmptyState, cx } from "../ui/primitives";

/** So'rov oralig'i — 30 soniya. */
const POLL_INTERVAL_MS = 30_000;

/** Ro'yxatda ko'rsatiladigan eng ko'p yozuv. */
const PREVIEW_LIMIT = 8;

export function NotificationBell(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const holder = useRef<HTMLDivElement>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      const result = await notificationsApi.list({ limit: PREVIEW_LIMIT });
      setItems(result.items);
      setUnreadCount(result.unreadCount);
    } catch {
      // Bildirishnoma yuklanmasa BUTUN QOBIQ ishlashda davom etadi: bu
      // yordamchi element va uning xatosi asosiy ishni to'xtatmasligi kerak.
    }
  }, []);

  useEffect(() => {
    void load();

    const timer = setInterval(() => {
      void load();
    }, POLL_INTERVAL_MS);

    return () => {
      clearInterval(timer);
    };
  }, [load]);

  // Tashqariga bosilganda yopiladi.
  useEffect(() => {
    if (!open) {
      return;
    }

    function onClick(event: MouseEvent): void {
      if (holder.current && !holder.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function onKey(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  async function markAllRead(): Promise<void> {
    await notificationsApi.markRead([]);
    await load();
  }

  return (
    <div ref={holder} className="relative">
      <button
        type="button"
        aria-label={
          unreadCount > 0
            ? `Bildirishnomalar (${String(unreadCount)} o'qilmagan)`
            : "Bildirishnomalar"
        }
        aria-expanded={open}
        onClick={() => {
          setOpen((value) => !value);
        }}
        className="relative rounded-icon p-2 text-body transition-colors hover:bg-brand-50 hover:text-ink"
      >
        <Bell className="size-[18px]" strokeWidth={1.85} aria-hidden="true" />

        {unreadCount > 0 ? (
          <span className="absolute right-1 top-1 flex min-w-[16px] items-center justify-center rounded-full bg-pink px-1 text-xs font-bold leading-4 text-white">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="absolute right-0 z-40 mt-1 w-[340px] animate-fade-up overflow-hidden rounded-panel bg-surface shadow-modal">
          <div className="flex items-center justify-between border-b border-hairline px-4 py-2.5">
            <span className="text-sm font-semibold text-ink">Bildirishnomalar</span>
            {unreadCount > 0 ? (
              <button
                type="button"
                onClick={() => void markAllRead()}
                className="text-xs font-medium text-brand hover:underline"
              >
                Hammasini o'qildi
              </button>
            ) : null}
          </div>

          {items.length === 0 ? (
            <EmptyState
              title="Bildirishnoma yo'q"
              description="Yangi ogohlantirish kelganda shu yerda ko'rinadi."
            />
          ) : (
            <ul className="max-h-[400px] divide-y divide-hairline overflow-y-auto">
              {items.map((item) => (
                <NotificationRow
                  key={item.id}
                  item={item}
                  onOpen={() => {
                    setOpen(false);
                  }}
                />
              ))}
            </ul>
          )}

          <div className="border-t border-hairline px-4 py-2">
            <Link
              to="/notifications"
              onClick={() => {
                setOpen(false);
              }}
              className="text-xs font-medium text-brand hover:underline"
            >
              Barchasini ko'rish →
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Ro'yxatdagi bitta yozuv. */
export function NotificationRow({
  item,
  onOpen,
}: {
  item: NotificationItem;
  onOpen?: () => void;
}): JSX.Element {
  const style = NOTIFICATION_TYPE_STYLE[item.type];

  const content = (
    <div
      className={cx(
        "px-4 py-3 transition-colors hover:bg-canvas",
        item.readAt === null && "bg-info-soft/40",
      )}
    >
      <div className="flex items-center gap-2">
        <Badge className={style?.className ?? "bg-subtle text-body ring-border"}>
          {style?.label ?? item.type}
        </Badge>
        {item.readAt === null ? <span className="size-1.5 rounded-full bg-pink" /> : null}
        <span className="ml-auto shrink-0 text-xs text-muted">
          {formatRelative(item.createdAt)}
        </span>
      </div>
      <p className="mt-1.5 text-sm font-medium leading-snug text-ink">{item.title}</p>
      {item.body ? <p className="mt-0.5 text-xs leading-relaxed text-body">{item.body}</p> : null}
    </div>
  );

  // Qo'ng'iroqqa bog'langan bildirishnoma bosilsa o'sha kartaga o'tadi:
  // ogohlantirishdan keyingi birinchi qadam doim «bu nima edi?» savoli.
  return (
    <li>
      {item.entityType === "call" && item.entityId !== null ? (
        <Link to={`/calls/${item.entityId}`} onClick={onOpen} className="block">
          {content}
        </Link>
      ) : (
        content
      )}
    </li>
  );
}

/** Sahifada ishlatiladigan «hammasini o'qildi» tugmasi. */
export function MarkAllReadButton({ onDone }: { onDone: () => void }): JSX.Element {
  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => {
        void notificationsApi.markRead([]).then(onDone);
      }}
    >
      Hammasini o'qildi
    </Button>
  );
}
